import React, { useState } from 'react';
import ResultCard from './ResultCard';
import { outputNodes, outputResultOf } from './graphModel';
import { pickFields } from './flowFields';
import { IMAGE_CHART_TYPES, renderStackedCharts, slugify } from '../export/chartExporter';

import { downloadCanvasAsPng } from '../downloadExport';

// The stored result of the last run, one card per output node, with the
// shared image export controls (background + stacked "large" download).
export default function ResultsPanel({ graph, metas, workflow, onExport }) {
  const [background, setBackground] = useState('theme');
  const [busy, setBusy] = useState(false);
  const [imageError, setImageError] = useState(null);
  const outputs = outputNodes(graph, metas);
  const result = workflow ? workflow.result : null;
  const exportItems = collectExportItems(outputs, result, metas);

  return (
    <section className="flow-results">
      <header className="flow-results-head">
        <h2 className="flow-panel-title">Results</h2>
        {result && result.ranAt ? (
          <span className="flow-results-ran">Last run {formatTime(result.ranAt)}</span>
        ) : null}
        <span className="flow-results-export">
          <select
            className="flow-results-bg"
            aria-label="Export background"
            value={background}
            onChange={(e) => setBackground(e.target.value)}
          >
            <option value="theme">Theme</option>
            <option value="white">White</option>
            <option value="black">Black</option>
            <option value="transparent">Transparent</option>
          </select>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={exportItems.length === 0 || busy}
            onClick={downloadCharts}
          >
            {busy ? 'Rendering…' : 'Download charts'}
          </button>
        </span>
      </header>
      {workflow && workflow.error ? <p className="flow-results-error">{workflow.error}</p> : null}
      {imageError ? <p className="flow-results-error">{imageError}</p> : null}
      {outputs.length === 0 ? (
        <p className="flow-results-hint">Add a chart or table node to see results here.</p>
      ) : (
        <div className="flow-results-grid">
          {outputs.map((node) => (
            <ResultCard
              key={node.id}
              node={node}
              meta={metas[node.type]}
              result={result}
              onExport={onExport}
              background={background}
            />
          ))}
        </div>
      )}
    </section>
  );

  // Renders all exportable charts onto one tall canvas and downloads it; maps
  // fetch their tiles first, so progress and failures show in the panel.
  async function downloadCharts() {
    setImageError(null);
    setBusy(true);
    try {
      const canvas = await renderStackedCharts(exportItems, { background });
      if (canvas)
        downloadCanvasAsPng(canvas, `${slugify((workflow && workflow.title) || 'charts')}.png`);
    } catch (error) {
      setImageError(error && error.message ? error.message : 'Could not render the images.');
    } finally {
      setBusy(false);
    }
  }
}

// Chart outputs that can be rendered to an image: canvas chart types with
// at least one row of the last run.
function collectExportItems(outputs, result, metas) {
  const items = [];
  for (const node of outputs) {
    const nodeResult = outputResultOf(result, node.id);
    const params = node.params || {};
    const chartType = params.chartType || 'table';
    const rows = (nodeResult && nodeResult.rows) || [];
    if (!IMAGE_CHART_TYPES.has(chartType) || !rows.length) continue;
    items.push({
      title: params.title || (metas[node.type] && metas[node.type].name) || node.type,
      chartType,
      rows,
      fields: pickFields(nodeResult && nodeResult.fields, params.columns),
      config: params.config || {}
    });
  }
  return items;
}

function formatTime(iso) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}
